import dayjs from 'dayjs';

// Three wire rules from the CORE-5266 backend that are easy to get wrong:
//
// 1. Dates are epoch milliseconds as a *value*, but ISO YYYY-MM-DD inside a
//    visibility condition. Crossing them is refused: err_invalid_date one way,
//    err_condition_value_invalid the other.
// 2. Percents carry the number the recruiter typed — 10 means 10%, not 0.1.
//    The engine divides by 100 itself; the wire never does.
// 3. A rule in `state: "error"` can still carry a `value` left by an earlier
//    save. Trust the state, not whether a value is present.

// An epoch that crosses as a string has to be read back as a number: dayjs
// parses "1807727400000" by its leading digits and answers the year 1813, so a
// calculated date landed centuries out (CORE-5487).
const asEpoch = (date: string | number | Date) =>
  typeof date === 'string' && /^-?\d+$/.test(date) ? Number(date) : date;

export const toDateValue = (date: string | number | Date | null) =>
  date === null || date === '' ? null : dayjs(asEpoch(date)).valueOf();

export const fromDateValue = (ms: number | null | undefined) =>
  ms === null || ms === undefined ? null : dayjs(ms).toDate();

// Visibility conditions compare dates as plain ISO days, not milliseconds.
export const toConditionDate = (date: string | number | Date | null) =>
  date === null || date === '' ? null : dayjs(date).format('YYYY-MM-DD');

// A percent crosses the wire exactly as typed; this exists to say so at the
// call site, so nobody "helpfully" divides it.
export const toPercentValue = (typed: number | string | null) =>
  typed === null || typed === '' ? null : Number(typed);

interface RuleState {
  state?: 'ok' | 'blocked' | 'error';
  value?: unknown;
}

// Whether a computed value is safe to show. Neither an errored rule's stale
// value nor a blocked one's is, however populated they look.
export const hasUsableValue = (rule: RuleState | null | undefined) =>
  Boolean(rule && rule.state === 'ok' && rule.value !== null);
